import { useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { useIsReducedMotion } from '@atlaskit/motion/use-is-reduced-motion';

import type { ContainerRect, MousePosition, UseMouseTrackingOptions } from './types';
import { useCanHover } from './use-can-hover';

/**
 * Hook for tracking mouse position relative to a container element.
 *
 * Provides requestAnimationFrame-batched position updates on mouse move and scroll events.
 * Automatically handles cleanup and respects reduced motion preferences.
 *
 * @example
 * ```tsx
 * useMouseTracking({
 *   containerRef: heroRef,
 *   wrapperRef: overlayRef,
 *   callbacks: {
 *     onPositionUpdate: (position, rect) => {
 *       overlayRef.current?.style.setProperty('--mouse-x', `${position.x}px`);
 *     },
 *     onMouseLeave: () => {
 *       overlayRef.current?.style.setProperty('--mouse-x', '-999px');
 *     },
 *   },
 * });
 * ```
 */
export function useMouseTracking({
	containerRef,
	wrapperRef,
	enabled = true,
	callbacks,
}: UseMouseTrackingOptions): {
	isReducedMotion: boolean;
	canHover: boolean;
} {
	const isReducedMotion = useIsReducedMotion();
	const canHover = useCanHover();

	// Store callbacks in ref to avoid re-binding on callback changes
	const callbacksRef = useRef(callbacks);
	callbacksRef.current = callbacks;

	useEffect(() => {
		if (
			typeof window === 'undefined' ||
			!wrapperRef.current ||
			isReducedMotion ||
			!canHover ||
			!enabled
		) {
			return;
		}

		const container = containerRef?.current || wrapperRef.current.parentElement;
		if (!container) {
			return;
		}

		let rafId: number | null = null;
		let lastMouseX = 0;
		let lastMouseY = 0;

		const updatePosition = () => {
			if (!wrapperRef.current || !container) {
				return;
			}

			// Cancel any pending RAF to batch updates
			if (rafId !== null) {
				cancelAnimationFrame(rafId);
			}

			rafId = requestAnimationFrame(() => {
				rafId = null;
				const containerRect = container.getBoundingClientRect();

				const position: MousePosition = {
					x: lastMouseX - containerRect.left,
					y: lastMouseY - containerRect.top,
					clientX: lastMouseX,
					clientY: lastMouseY,
				};

				const rect: ContainerRect = {
					top: containerRect.top,
					left: containerRect.left,
					right: containerRect.right,
					bottom: containerRect.bottom,
					width: containerRect.width,
					height: containerRect.height,
				};

				callbacksRef.current.onPositionUpdate(position, rect);
			});
		};

		const handleMouseMove = (e: MouseEvent) => {
			lastMouseX = e.clientX;
			lastMouseY = e.clientY;
			updatePosition();
		};

		const handleMouseLeave = () => {
			callbacksRef.current.onMouseLeave?.();
		};

		// Track mouse globally to continue tracking when scrolling
		const unbindMove = bind(window, {
			type: 'mousemove',
			listener: handleMouseMove,
		});

		// Track scroll events to update position as container moves
		const unbindScroll = bind(window, {
			type: 'scroll',
			listener: updatePosition,
			options: { passive: true },
		});

		// Track scrollend for final position update when scrolling stops
		const unbindScrollEnd = bind(window, {
			type: 'scrollend',
			listener: updatePosition,
			options: { passive: true },
		});

		// Track mouse leave on container
		const unbindLeave = bind(container, {
			type: 'mouseleave',
			listener: handleMouseLeave,
		});

		return () => {
			unbindMove();
			unbindScroll();
			unbindScrollEnd();
			unbindLeave();
			if (rafId !== null) {
				cancelAnimationFrame(rafId);
			}
		};
	}, [containerRef, wrapperRef, isReducedMotion, canHover, enabled]);

	return { isReducedMotion, canHover };
}
